import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronLeft,
  ChevronRight,
  GalleryHorizontalEnd,
  Clapperboard,
  Image as ImageIcon,
  Layers,
  CalendarClock,
} from "lucide-react";
import { issuesApi, type Issue } from "@/api/issues";

/**
 * Calendário visual de posts (aba Acompanhamento do Painel) — estilo Google
 * Calendar, por empresa. Lê as issues com `scheduledFor` e mostra cada post no
 * seu dia. Navegação por mês (‹ ›) + "Hoje".
 */

const WEEKDAYS = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];

const TEMPLATE_ICON: Record<string, typeof Layers> = {
  carrossel: GalleryHorizontalEnd,
  reels: Clapperboard,
  post_instagram: ImageIcon,
  stories: Layers,
};

/** Cor do post por status — leitura de relance do estado de cada peça. */
const STATUS_DOT: Record<string, string> = {
  backlog: "var(--wf-ink-4)",
  todo: "var(--wf-ink-4)",
  in_progress: "var(--nd-orange)",
  in_review: "var(--nd-blue)",
  done: "#3aa657",
  blocked: "#d92d4a",
};

function dateKey(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function addDays(d: Date, n: number): Date {
  const c = new Date(d);
  c.setDate(c.getDate() + n);
  return c;
}

export function CalendarBoard({ companyId }: { companyId: string }) {
  const navigate = useNavigate();
  const now = new Date();
  const [view, setView] = useState(() => new Date(now.getFullYear(), now.getMonth(), 1));

  const issuesQuery = useQuery({
    queryKey: ["companies", companyId, "issues", { scheduled: true }],
    queryFn: () => issuesApi.list(companyId, { includeRoutineExecutions: false }),
    enabled: !!companyId,
    staleTime: 30_000,
  });

  // Posts (issues com scheduledFor) agrupados por dia.
  const byDay = useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const i of issuesQuery.data ?? []) {
      if (!i.scheduledFor) continue;
      const key = dateKey(new Date(i.scheduledFor));
      const arr = map.get(key) ?? [];
      arr.push(i);
      map.set(key, arr);
    }
    return map;
  }, [issuesQuery.data]);

  const totalScheduled = useMemo(
    () => (issuesQuery.data ?? []).filter((i) => i.scheduledFor).length,
    [issuesQuery.data],
  );

  // Grade do mês: começa no domingo da semana do dia 1.
  const year = view.getFullYear();
  const month = view.getMonth();
  const monthStart = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const startWeekday = monthStart.getDay();
  const weeks = Math.ceil((startWeekday + daysInMonth) / 7);
  const gridStart = addDays(monthStart, -startWeekday);
  const cells = Array.from({ length: weeks * 7 }, (_, i) => addDays(gridStart, i));
  const todayKey = dateKey(now);

  const monthLabel = view.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });

  function openIssue(issue: Issue) {
    const done = issue.status === "done" || issue.status === "in_review";
    navigate(done ? `/resultado/${issue.id}` : `/execucao/${issue.id}`);
  }

  return (
    <div>
      {/* Toolbar */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        <div
          className="wf-display"
          style={{ fontSize: 20, fontWeight: 700, textTransform: "capitalize", minWidth: 180 }}
        >
          {monthLabel}
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          <button type="button" className="cal-nav" aria-label="Mês anterior"
            onClick={() => setView(new Date(year, month - 1, 1))}>
            <ChevronLeft size={16} strokeWidth={2} />
          </button>
          <button type="button" className="cal-nav" aria-label="Próximo mês"
            onClick={() => setView(new Date(year, month + 1, 1))}>
            <ChevronRight size={16} strokeWidth={2} />
          </button>
        </div>
        <button
          type="button"
          className="wf-btn wf-btn--ghost wf-btn--sm"
          onClick={() => setView(new Date(now.getFullYear(), now.getMonth(), 1))}
        >
          Hoje
        </button>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 12.5, color: "var(--wf-ink-3)" }}>
          {totalScheduled} {totalScheduled === 1 ? "post agendado" : "posts agendados"}
        </span>
      </div>

      <style>{`
        .cal-nav { width: 32px; height: 32px; border-radius: 8px; border: 1px solid var(--wf-ink-5);
          background: var(--wf-paper); color: var(--wf-ink-2); display: flex; align-items: center;
          justify-content: center; cursor: pointer; }
        .cal-nav:hover { border-color: var(--nd-orange); color: var(--nd-orange); }
        /* minmax(0,1fr): colunas iguais mesmo com título longo (sem o minmax,
           1fr = minmax(auto,1fr) e o conteúdo nowrap estoura a coluna). */
        .cal-grid { display: grid; grid-template-columns: repeat(7, minmax(0, 1fr)); }
        /* Altura FIXA — o grid não redimensiona; os cards se adaptam/rolam dentro. */
        .cal-cell { height: 116px; border-right: 1px solid var(--wf-ink-5);
          border-bottom: 1px solid var(--wf-ink-5); padding: 6px 7px; display: flex;
          flex-direction: column; gap: 4px; background: var(--wf-paper); overflow: hidden; }
        .cal-cell.dim { background: var(--wf-bg); }
        .cal-cell-posts { flex: 1; min-height: 0; overflow-y: auto; display: flex;
          flex-direction: column; gap: 4px; }
        .cal-cell-posts::-webkit-scrollbar { width: 4px; }
        .cal-cell-posts::-webkit-scrollbar-thumb { background: var(--wf-ink-5); border-radius: 4px; }
        .cal-post { display: flex; align-items: center; gap: 5px; padding: 3px 6px; border-radius: 7px;
          background: var(--wf-bg); cursor: pointer; font-size: 11px; line-height: 1.15;
          border: 1px solid var(--wf-ink-5); min-width: 0; max-width: 100%; flex-shrink: 0; }
        .cal-post:hover { border-color: var(--nd-orange); }
      `}</style>

      {/* Grade */}
      <div
        style={{
          border: "1px solid var(--wf-ink-5)",
          borderRadius: 14,
          overflow: "hidden",
          background: "var(--wf-paper)",
        }}
      >
        {/* Cabeçalho dos dias da semana */}
        <div className="cal-grid">
          {WEEKDAYS.map((w) => (
            <div
              key={w}
              style={{
                padding: "9px 8px",
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: "0.04em",
                textTransform: "uppercase",
                color: "var(--wf-ink-3)",
                borderRight: "1px solid var(--wf-ink-5)",
                borderBottom: "1px solid var(--wf-ink-5)",
                textAlign: "center",
                background: "var(--wf-bg)",
              }}
            >
              {w}
            </div>
          ))}
        </div>

        {/* Células */}
        <div className="cal-grid">
          {cells.map((d) => {
            const key = dateKey(d);
            const inMonth = d.getMonth() === month;
            const isToday = key === todayKey;
            const posts = byDay.get(key) ?? [];
            return (
              <div key={key} className={`cal-cell ${inMonth ? "" : "dim"}`}>
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: isToday ? 800 : 500,
                      color: isToday ? "#fff" : inMonth ? "var(--wf-ink-2)" : "var(--wf-ink-4)",
                      background: isToday ? "var(--nd-orange)" : "transparent",
                      width: 22,
                      height: 22,
                      borderRadius: 999,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {d.getDate()}
                  </span>
                </div>
                <div className="cal-cell-posts">
                  {posts.map((p) => {
                    const Icon = TEMPLATE_ICON[p.templateKey ?? ""] ?? CalendarClock;
                    return (
                      <div key={p.id} className="cal-post" onClick={() => openIssue(p)} title={p.title}>
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: 999,
                            background: STATUS_DOT[p.status] ?? "var(--wf-ink-4)",
                            flexShrink: 0,
                          }}
                        />
                        <Icon size={11} strokeWidth={2} style={{ flexShrink: 0, color: "var(--wf-ink-3)" }} />
                        <span
                          style={{
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {p.title}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legenda + estado vazio */}
      {totalScheduled === 0 && !issuesQuery.isLoading && (
        <div
          style={{
            marginTop: 14,
            padding: "16px 18px",
            borderRadius: 12,
            border: "1px dashed var(--wf-ink-5)",
            fontSize: 13,
            color: "var(--wf-ink-3)",
            lineHeight: 1.5,
          }}
        >
          Nenhum post agendado ainda. Gere um <strong style={{ color: "var(--wf-ink-2)" }}>calendário
          editorial</strong> e clique em “Criar tarefas” — os posts aparecem aqui, cada um no seu dia.
        </div>
      )}
    </div>
  );
}
